import { translateDocumentType } from "@asap/shared/utils/translate";
import { z } from "zod";
import { documentTypes, jobRisks, mealTypes } from "~/supabase.values";
import { buildTypeIssueMessage, invalid, required } from "./helpers";

export const schemaAddress = z.object(
  {
    street1: z.string({
      error: buildTypeIssueMessage({
        whenInvalidType: "street1: Il y a une erreur avec l'adresse, supprimez la et rajoutez la à nouveau.",
      }),
    }),
    street2: z.string({
      error: buildTypeIssueMessage({
        whenInvalidType: "street2: Il y a une erreur avec l'adresse, supprimez la et rajoutez la à nouveau.",
      }),
    }),
    formatted: z.string({
      error: buildTypeIssueMessage({
        whenInvalidType: "formatted: Il y a une erreur avec l'adresse, supprimez la et rajoutez la à nouveau.",
      }),
    }),
    city: z.string({
      error: buildTypeIssueMessage({
        whenInvalidType: "city: Il y a une erreur avec l'adresse, supprimez la et rajoutez la à nouveau.",
      }),
    }),
    postal_code: z.string({
      error: buildTypeIssueMessage({
        whenInvalidType: "postal_code: Il y a une erreur avec l'adresse, supprimez la et rajoutez la à nouveau.",
      }),
    }),
    region: z.string({
      error: buildTypeIssueMessage({
        whenMissing: "Sélectionner de nouveau l'adresse car la région a mal été enregistrée",
        whenInvalidType: "region: Il y a une erreur avec l'adresse, supprimez la et rajoutez la à nouveau.",
      }),
    }),
    department: z.string({
      error: buildTypeIssueMessage({
        whenMissing: "Sélectionner de nouveau l'adresse car le département a mal été enregistré",
        whenInvalidType: "department: Il y a une erreur avec l'adresse, supprimez la et rajoutez la à nouveau.",
      }),
    }),
    country: z.string({
      error: buildTypeIssueMessage({
        whenInvalidType: "country: Il y a une erreur avec l'adresse, supprimez la et rajoutez la à nouveau.",
      }),
    }),
    coordinates: z.object(
      {
        lat: z.number(),
        lng: z.number(),
      },
      {
        error: buildTypeIssueMessage({
          whenInvalidType: "coordinates: Il y a une erreur avec l'adresse, supprimez la et rajoutez la à nouveau.",
        }),
      }
    ),
  },
  {
    error: buildTypeIssueMessage({
      whenMissing: required("L'adresse"),
      whenInvalidType: "Veuillez taper manuellement l'adresse.",
    }),
  }
);

/** When you only need an address for a relative position, not the exact one
 * For instance, to search something around a given neighborhood)
 * If you need the exact address (for a billing address for instance), use schemaAddress
 **/
export const partialAddressSchema = schemaAddress.extend({
  street1: z
    .string({
      error: buildTypeIssueMessage({
        whenInvalidType: "Il y a une erreur avec l'adresse, supprimez la et rajoutez la à nouveau.",
      }),
    })
    .nullable()
    .default(""),
  street2: z
    .string({
      error: buildTypeIssueMessage({
        whenInvalidType: "Il y a une erreur avec l'adresse, supprimez la et rajoutez la à nouveau.",
      }),
    })
    .nullable()
    .default(""),
});

export const schemaLocation = z
  .string({ error: buildTypeIssueMessage({ whenMissing: required("La localisation") }) })
  .nullable();
export const schemaEmail = z
  .email({
    error: buildTypeIssueMessage({
      whenMissing: required("L'email"),
      whenInvalidType: "L'email doit être une chaîne de caractères",
      whenInvalidFormat: invalid("email"),
    }),
  })
  .toLowerCase();
export const schemaPhoneNumber = z.string({
  error: buildTypeIssueMessage({
    whenMissing: required("Le numéro de téléphone"),
    whenInvalidType: "Le numéro de téléphone n'est pas valide",
  }),
});
export const schemaRisks = z.array(
  z.enum(jobRisks, { error: buildTypeIssueMessage({ whenMissing: required("Les risques") }) })
);
export const schemaOtherRisks = z.string().optional();
export const schemaTalentId = z.string({ error: buildTypeIssueMessage({ whenMissing: required("Le talent") }) });
export const schemaMissionId = z.string({ error: buildTypeIssueMessage({ whenMissing: required("La mission") }) });
export const schemaAgencyId = z.string({ error: buildTypeIssueMessage({ whenMissing: required("L'agence") }) });
export const schemaOwnerId = z.string({ error: buildTypeIssueMessage({ whenMissing: required("Le consultant") }) });
export const schemaAdminId = z.string().optional();
export const schemaContractId = z.string({ error: buildTypeIssueMessage({ whenMissing: required("Le contrat") }) });
export const schemaWorkspaceId = z.string({
  error: buildTypeIssueMessage({ whenMissing: required("L'espace de travail") }),
});
export const schemaWsmId = z.string({ error: buildTypeIssueMessage({ whenMissing: required("Le WSM") }) });
export const schemaMealCardType = z.enum(mealTypes, {
  error: buildTypeIssueMessage({ whenMissing: required("Le mode de restauration") }),
});
export const schemaMealCardValue = z
  .number({ error: buildTypeIssueMessage({ whenMissing: required("La valeur faciale des tickets restaurant") }) })
  .nullable();

const schemaFileSupabase = z.object({
  name: z.string().nullable(),
  mimetype: z.string().nullable(),
  supabase_bucket_id: z.string().nullable(),
  supabase_name: z.string().nullable(),
});

export const schemaDocumentSupabase = z.object(
  {
    id: z.string(),
    type: z.enum(documentTypes),
    files: z.array(schemaFileSupabase).nonempty({ message: required("Le fichier") }),
    expiration_date: z.string().nullable(),
  },
  {
    error: (issue) => {
      const path = issue.path?.toString() ?? "";
      if (path === "identity_proof_document") return required("Le justificatif d'identité");
      if (path === "proof_of_address_document") return required("Le justificatif de domicile");
      return required(translateDocumentType.fr[path as keyof typeof translateDocumentType.fr]);
    },
  }
);
